import { CustomObject } from '@shared/modules/custom-objects/custom-objects.types';
import { JupiterStateFeatureSupportModel, JupiterStateModel, LabelsSlot, BulkUpdateParams } from './types';
import { RelatedToType } from '@shared/enums';
import { FeatureFlag } from '@core/enums';

export const isAddFormDefinitionPersisted = (jupiterState: JupiterStateModel): boolean => {
  return (
    jupiterState.isCustom ||
    [RelatedToType.PropertyTenant, RelatedToType.Company, RelatedToType.Contact, RelatedToType.Property].includes(
      jupiterState.relatedToObject.name as RelatedToType,
    )
  );
};

export const getLabelSlots = (tsObject: CustomObject): LabelsSlot => {
  return {
    ADD: tsObject.label ? `Add new ${tsObject.label}` : 'Add',
    EXPORT_CSV: 'Export as CSV',
    IMPORT_VCARD: 'Import VCard',
    BULK_ACTIONS: 'Update Selected',
  };
};

export const getFeatureSupport = (
  obj: CustomObject,
  featureFlags: Record<FeatureFlag, boolean> = {} as Record<FeatureFlag, boolean>,
  canManageApprovals = false,
): JupiterStateFeatureSupportModel => {
  if (obj.id) {
    return {
      groupedTable: false,
      bulkActions: {
        enabled: true,
        actions: {
          delete: true,
          updateTags: false,
          createDealOrPortfolio: false,
          update: true,
          duplicate: true,
          merge: true,
          changeLayout: true,
        },
      },
      homeActions: {
        actions: {
          add: true,
          export: true,
          importVCard: false,
          createContactAssociation: false,
          manageView: true,
          viewList: true,
          linkDeal: false,
          groupBy: true,
        },
      },
      gridActions: {
        enabled: true,
        actions: {
          logCall: false,
          logMeeting: false,
          logSocialMedia: false,
          edit: true,
          delete: true,
          openDetails: true,
          duplicate: true,
        },
      },
      tags: {
        enabled: false,
      },
      detailsMainTabs: {
        backButton: true,
        avatar: false,
        details: true,
        files: true,
        notes: true,
        activities: true,
        engagements: false,
        tasks: true,
        linkedObjects: true,
        deals: false,
        leases: false,
        nearby: false,
        contacts: false,
        dispositions: false,
        loanQuotes: false,
        financials: false,
        approvals: false,
        contextMenu: {
          enabled: true,
          actions: {
            layoutManager: true,
            delete: true,
            duplicate: true,
            changeLayout: true,
            generateMergeDocument: true,
            merge: true,
            sendEmail: false,
            summary: false,
          },
        },
      },
      panels: {
        'crm-table-panel': false,
        'crm-action-panel': false,
        form: true,
        comments: true,
        table: true,
        images: true,
        matrix: true,
        activity: true,
        engagements: true,
        map: false,
        chart: false,
        financial_report: false,
        roles: false,
        contacts: false,
        unit_lease: false,
        unit_form: false,
        mailbox: false,
      },
    };
  }

  const isPropertyNearby = obj.uiConfig?.urlPrefix === 'property-nearby';
  const isPropertyDeals = obj.uiConfig?.urlPrefix === 'property-deals';
  const isCrm = [RelatedToType.Contact, RelatedToType.Company].includes(obj.name as RelatedToType);
  const isContact = RelatedToType.Contact === (obj.name as RelatedToType);
  const canShowLeases = ![RelatedToType.Contact, RelatedToType.Company, RelatedToType.PropertyTenant].includes(
    obj.name as RelatedToType,
  );
  const canShowGalleryPanel = canShowLeases;
  const canShowMatrixPanel = true;
  const canShowTablePanel = RelatedToType.PropertyTenant !== obj.name;
  const canShowAddButton = RelatedToType.PropertyTenant !== obj.name && !isPropertyNearby;
  const canShowAssetManagement = [RelatedToType.Property, RelatedToType.PropertyTenant].includes(
    obj.name as RelatedToType,
  );
  const canBulkMerge = [
    RelatedToType.Company,
    RelatedToType.Contact,
    RelatedToType.Property,
    RelatedToType.PropertyTenant,
  ].includes(obj.name as RelatedToType);

  return {
    groupedTable: false,
    bulkActions: {
      enabled: true,
      actions: {
        delete: true,
        updateTags: false, // we are removing the support for updating tags for now
        createDealOrPortfolio: RelatedToType.Property === obj.name,
        update: true,
        duplicate: true,
        merge: canBulkMerge,
        changeLayout: true,
      },
    },
    homeActions: {
      actions: {
        add: canShowAddButton,
        export: !isPropertyNearby && !isPropertyDeals,
        importVCard: isContact && !obj.uiConfig?.isForStandardRelation,
        createContactAssociation: RelatedToType.Contact === obj.name && obj.uiConfig?.isForStandardRelation,
        manageView: !isPropertyNearby && !isPropertyDeals,
        viewList: !isPropertyNearby && !isPropertyDeals,
        linkDeal: isPropertyDeals,
        groupBy: true,
      },
    },
    gridActions: {
      enabled: true,
      actions: {
        logCall: isCrm,
        logMeeting: isCrm,
        logSocialMedia: isCrm,
        edit: false,
        delete: true,
        openDetails: true,
        duplicate: true,
      },
    },
    tags: {
      enabled: false, // we are removing the support for tags for now
    },
    detailsMainTabs: {
      backButton: true,
      avatar: isCrm,
      details: true,
      files: true,
      notes: true,
      activities: true,
      engagements: isCrm,
      tasks: RelatedToType.PropertyTenant !== obj.name,
      linkedObjects: RelatedToType.PropertyTenant !== obj.name,
      leases: canShowLeases,
      deals: RelatedToType.Property === obj.name,
      nearby: RelatedToType.Property === obj.name,
      loanQuotes: RelatedToType.Project === obj.name,
      dispositions: RelatedToType.Project === obj.name,
      contacts: RelatedToType.Project === obj.name,
      financials: canShowAssetManagement,
      approvals: featureFlags[FeatureFlag.DEAL_APPROVALS] && canManageApprovals && RelatedToType.Project === obj.name,
      contextMenu: {
        enabled: true,
        actions: {
          layoutManager: true,
          delete: true,
          duplicate: false,
          changeLayout: true,
          generateMergeDocument: RelatedToType.Property === obj.name,
          merge: true,
          sendEmail: RelatedToType.Contact === obj.name,
          summary: RelatedToType.Property === obj.name,
        },
      },
    },
    panels: {
      'crm-table-panel': isCrm || RelatedToType.Property === obj.name, // I think I will refactor this soon
      'crm-action-panel': isCrm,
      form: true,
      comments: true,
      table: canShowTablePanel,
      images: canShowGalleryPanel,
      matrix: canShowMatrixPanel,
      activity: true,
      engagements: isCrm,
      map: RelatedToType.Property === obj.name,
      chart: canShowAssetManagement,
      financial_report: canShowAssetManagement,
      roles: [RelatedToType.Property, RelatedToType.Project].includes(obj.name as RelatedToType),
      contacts: RelatedToType.Property === obj.name,
      unit_lease: RelatedToType.Property === obj.name,
      unit_form: RelatedToType.Property === obj.name,
      mailbox: isCrm,
    },
  };
};

export const JUPITER_TABLE_SYSTEM_COLUMN_IDS = new Set([
  'openDetails',
  'checkbox',
  'actions',
  'ag-Grid-SelectionColumn',
]);
export const JUPITER_TABLE_AUTO_GROUP_COLUMN_ID = 'ag-Grid-AutoColumn';

type JupiterSortableField = {
  name?: string;
  meta?: { sorting?: string };
};

export type JupiterGridColumnLike = {
  getColId(): string;
  getColDef(): { field?: string; context?: { name?: string } };
};

export const resolveJupiterAutoGroupFieldName = (
  autoGroupColumn: JupiterGridColumnLike | null | undefined,
): string | null => {
  if (!autoGroupColumn) {
    return null;
  }

  const colDef = autoGroupColumn.getColDef();

  return colDef.context?.name ?? colDef.field ?? null;
};

export const getJupiterColumnMoveKey = (column: JupiterGridColumnLike): string => {
  const colId = column.getColId();

  if (colId === JUPITER_TABLE_AUTO_GROUP_COLUMN_ID) {
    return resolveJupiterAutoGroupFieldName(column) ?? colId;
  }

  const colDef = column.getColDef();

  return colDef.context?.name ?? colDef.field ?? colId;
};

type JupiterColumnStateLike = {
  colId: string;
};

const isJupiterGridSystemColumnId = (colId: string): boolean => {
  if (JUPITER_TABLE_SYSTEM_COLUMN_IDS.has(colId)) {
    return true;
  }

  return colId.startsWith('ag-Grid-') && colId !== JUPITER_TABLE_AUTO_GROUP_COLUMN_ID;
};

export const resolveDisplayColumnsFromColumnState = (
  columnState: JupiterColumnStateLike[],
  autoGroupFieldName?: string | null,
): string[] => {
  const orderedDisplayColumns = columnState.reduce<string[]>((acc, { colId }) => {
    if (isJupiterGridSystemColumnId(colId)) {
      return acc;
    }

    if (colId === JUPITER_TABLE_AUTO_GROUP_COLUMN_ID) {
      if (autoGroupFieldName) {
        acc.push(autoGroupFieldName);
      }

      return acc;
    }

    acc.push(colId);

    return acc;
  }, []);

  if (!autoGroupFieldName) {
    return orderedDisplayColumns;
  }

  const otherFields = orderedDisplayColumns.filter((name) => name !== autoGroupFieldName);

  return [autoGroupFieldName, ...otherFields];
};

const STRUCTURAL_FIELDS = new Set([
  'id',
  'key',
  'parentKey',
  '_rowType',
  '_groupCellValue',
  'group_path',
  'meta',
  'permissions',
]);

type JupiterRow = Record<string, unknown> & {
  id?: string | number | null;
  key?: string | number | null;
  parentKey?: string | number | null;
  _rowType?: string;
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

const getNodeKey = (row: JupiterRow): string | number | null => {
  return row.key ?? row.id ?? null;
};

const detectNumericFields = (rows: JupiterRow[]): string[] => {
  const numericFields = new Set<string>();
  const recordRows = rows.filter((row) => row?._rowType === 'record');

  for (const row of recordRows) {
    for (const [field, value] of Object.entries(row)) {
      if (STRUCTURAL_FIELDS.has(field)) {
        continue;
      }

      if (toNumber(value) !== null) {
        numericFields.add(field);
      }
    }
  }

  return [...numericFields];
};

type GroupPathNode = {
  key: string;
  label: string;
  parentKey?: string | number | null;
  [key: string]: unknown;
};

type GroupBySearchEdge = {
  node: Record<string, unknown>;
  groupPath: GroupPathNode[];
};

export const mapGroupBySearchResultsToRows = (rows: GroupBySearchEdge[], fieldNames: string[]): JupiterRow[] => {
  const flattened: JupiterRow[] = rows.map((result) => ({
    ...result.node,
    _rowType: 'record',
    groupPath: result.groupPath.map((groupPathNode, index) => ({
      ...groupPathNode,
      parentKey: index === 0 ? null : result.groupPath[index - 1].key,
    })),
    parentKey: result.groupPath[result.groupPath.length - 1]?.key ?? null,
  }));

  const [firstField] = fieldNames;
  const uniqueGroupPaths = flattened.reduce<JupiterRow[]>((accumulator, current) => {
    const groupPath = (current as JupiterRow & { groupPath?: GroupPathNode[] }).groupPath ?? [];

    for (const group of groupPath) {
      if (accumulator.some((existing) => existing.key === group.key)) {
        continue;
      }

      const groupRow: JupiterRow = {
        ...group,
        key: group.key,
        _rowType: 'group',
        _groupCellValue: group.label,
      };

      if (firstField) {
        groupRow[firstField] = group.label;
      }

      if (firstField !== 'id') {
        groupRow.id = group.key;
      }

      accumulator.push(groupRow);
    }

    return accumulator;
  }, []);

  return [...flattened, ...uniqueGroupPaths];
};

export const getJupiterGroupedRowId = (row: JupiterRow | undefined): string | undefined => {
  if (!row) {
    return undefined;
  }

  if (row._rowType === 'group') {
    return row.key?.toString() ?? row.id?.toString();
  }

  return row.id?.toString();
};

export const addGroupAggregations = (rows: JupiterRow[]): JupiterRow[] => {
  const childrenByParentKey = new Map<string | number | null, JupiterRow[]>();
  const numericFields = detectNumericFields(rows);

  for (const row of rows) {
    const parentKey = row?.parentKey ?? null;

    if (!childrenByParentKey.has(parentKey)) {
      childrenByParentKey.set(parentKey, []);
    }

    childrenByParentKey.get(parentKey)?.push(row);
  }

  const recordCache = new Map<string | number | null, JupiterRow[]>();

  const collectDescendantRecords = (groupKey: string | number | null): JupiterRow[] => {
    if (recordCache.has(groupKey)) {
      return recordCache.get(groupKey) ?? [];
    }

    const children = childrenByParentKey.get(groupKey) ?? [];
    const records: JupiterRow[] = [];

    for (const child of children) {
      if (child?._rowType === 'record') {
        records.push(child);
        continue;
      }

      if (child?._rowType === 'group') {
        const childKey = getNodeKey(child);

        if (childKey !== null) {
          records.push(...collectDescendantRecords(childKey));
        }
      }
    }

    recordCache.set(groupKey, records);

    return records;
  };

  return rows.map((row) => {
    if (row?._rowType !== 'group') {
      return row;
    }

    const groupKey = getNodeKey(row);
    const descendantRecords = groupKey === null ? [] : collectDescendantRecords(groupKey);
    const aggregations: Record<string, { total: number; average: number | null }> = {};

    for (const field of numericFields) {
      let sum = 0;
      let count = 0;

      for (const record of descendantRecords) {
        const numericValue = toNumber(record[field]);

        if (numericValue === null) {
          continue;
        }

        sum += numericValue;
        count += 1;
      }

      aggregations[field] = {
        total: sum,
        average: count > 0 ? sum / count : null,
      };
    }

    return {
      ...row,
      summaryColumns: aggregations,
    };
  });
};

export type JupiterTableSort = { field: string; order: 'asc' | 'desc' };

type JupiterColumnSortState = {
  colId: string;
  sort?: 'asc' | 'desc' | null;
};

export const resolveJupiterTableSortField = (colId: string, autoGroupField?: JupiterSortableField | null): string => {
  if (colId !== JUPITER_TABLE_AUTO_GROUP_COLUMN_ID) {
    return colId;
  }

  return autoGroupField?.meta?.sorting ?? autoGroupField?.name ?? colId;
};

export const resolveJupiterTableSortFromColumnState = (
  columnState: JupiterColumnSortState[] | null | undefined,
  autoGroupField?: JupiterSortableField | null,
): JupiterTableSort[] => {
  const sortState = columnState?.find((col) => col.sort);

  if (!sortState?.sort) {
    return [];
  }

  return [
    {
      field: resolveJupiterTableSortField(sortState.colId, autoGroupField),
      order: sortState.sort,
    },
  ];
};

export type JupiterBulkEditItem = {
  id: string | number;
  [key: string]: unknown;
};

export const buildJupiterBulkUpdateParams = (items: JupiterBulkEditItem[]): BulkUpdateParams => {
  const recordIds = items.map((item) => Number(item.id));
  const { id: _id, ...actionParams } = items[0] ?? {};

  return {
    actionType: 'update_field',
    recordIds,
    actionParams,
  };
};

export const buildJupiterBulkChangeLayoutParams = (recordIds: number[], templateId: number): BulkUpdateParams => ({
  actionType: 'change_layout',
  recordIds,
  actionParams: { template_id: templateId },
});

export const buildJupiterBulkDeleteParams = (recordIds: number[]): BulkUpdateParams => ({
  actionType: 'delete',
  recordIds,
});

export const buildJupiterBulkDuplicateParams = (recordIds: number[]): BulkUpdateParams => ({
  actionType: 'duplicate',
  recordIds,
});

export const buildJupiterBulkMergeParams = (sourceRecordIds: number[], destinationId: number): BulkUpdateParams => ({
  actionType: 'merge',
  recordIds: sourceRecordIds,
  actionParams: { destination_id: destinationId },
});
